"use client";

import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { VStack } from "@astryxdesign/core/VStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import {
  LandingCasesCarouselNav,
  LandingCasesCatalog,
  useCasesCarouselScroll,
} from "@/components/landing/cases-catalog";
import { LandingSectionBg } from "@/components/landing/landing-section-bg";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import { LANDING_PROSE_MAX, LANDING_SECTION_MAX } from "@/lib/landing/layout";
import { DEFAULT_CASES } from "@/lib/landing/puck/defaults";
import type { LandingCaseItem } from "@/components/landing/cases-view";
import {
  landingTextProps,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingCasesBodyProps = {
  sectionId?: string;
  title?: LandingTextValue;
  subtitle?: LandingTextValue;
  backgroundImageUrl?: string;
  backgroundColor?: string;
  layout?: "grid" | "stack" | "carousel";
  items: LandingCaseItem[];
};

/** Клиентская обёртка: карусель с навигацией в шапке секции. */
export function LandingCasesBody({
  sectionId = DEFAULT_CASES.sectionId,
  title = DEFAULT_CASES.title,
  subtitle = DEFAULT_CASES.subtitle,
  backgroundImageUrl = DEFAULT_CASES.backgroundImageUrl,
  backgroundColor,
  layout = "grid",
  items,
}: LandingCasesBodyProps) {
  const isCarousel = layout === "carousel";
  const { scrollRef, scrollByPage } = useCasesCarouselScroll();

  return (
    <LandingSectionBg backgroundImageUrl={backgroundImageUrl} backgroundColor={backgroundColor}>
      <Section id={sectionId} variant="muted" paddingBlock={10}>
        <Center>
          <VStack
            width="100%"
            maxWidth={LANDING_SECTION_MAX}
            gap={8}
            align="start"
            className="min-w-0 px-6"
          >
            <div className="flex w-full items-start justify-between gap-4">
              <VStack maxWidth={LANDING_PROSE_MAX} gap={4} align="start" className="min-w-0">
                <LandingCanvasText field="title" value={title} label="Заголовок" multiline>
                  <Heading level={2} type="display-3" {...landingTextProps(title)} />
                </LandingCanvasText>
                <LandingCanvasText
                  field="subtitle"
                  value={subtitle}
                  label="Подзаголовок"
                  multiline
                  hideIfEmpty
                >
                  <Text color="secondary" {...landingTextProps(subtitle)} />
                </LandingCanvasText>
              </VStack>
              {isCarousel ? (
                <LandingCasesCarouselNav
                  onPrev={() => scrollByPage(-1)}
                  onNext={() => scrollByPage(1)}
                />
              ) : null}
            </div>

            <div className="w-full min-w-0">
              <LandingCasesCatalog
                items={items}
                layout={layout}
                scrollRef={isCarousel ? scrollRef : undefined}
              />
            </div>
          </VStack>
        </Center>
      </Section>
    </LandingSectionBg>
  );
}
